import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Icon from "./icons";
import {
  CODE_LENGTH, codeErrorMessage, formatSeconds, isEmail, requestSignInCode,
  useResendCountdown, verifySignInCode,
} from "./signInCode";
import "../../styles/signInCodeFlow.css";

// "Sign in with a code": type your email, get a 6-digit code, type it, you're in.
// One flow for both sign-in screens (custom_auth/sign_in_codes.py):
//   student: LoginModal, variant "student" (yellow and brown, like the chat)
//   staff:   GuidanceLogin, variant "staff" (the admin portal's white and amber)
// onSignedIn({ access, refresh }) finishes the sign-in the same way a password does.

const EMPTY = Array(CODE_LENGTH).fill("");
const RESEND_SECONDS = 30;
const EASE = [0.16, 1, 0.3, 1];

const Spinner = () => <span className="sic-spinner" aria-hidden="true" />;

// The countdown before "Send a new code" works, as a ring that empties
const CountdownRing = ({ left }) => {
  const r = 7;
  const c = 2 * Math.PI * r;
  return (
    <svg className="sic-ring" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <circle cx="9" cy="9" r={r} className="sic-ring-track" />
      <circle
        cx="9" cy="9" r={r}
        className="sic-ring-left"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - left / RESEND_SECONDS)}
      />
    </svg>
  );
};

const SignInCodeFlow = ({ portal, variant = "student", initialEmail = "", onSignedIn, onBack }) => {
  const reduce = useReducedMotion();
  const [step, setStep] = useState("email"); // email, code, done
  const [direction, setDirection] = useState(1);
  const [email, setEmail] = useState(initialEmail.trim());
  const [emailError, setEmailError] = useState("");
  const [sending, setSending] = useState(false);
  const [ticket, setTicket] = useState(null); // { token, sent_to }
  const [digits, setDigits] = useState(EMPTY);
  const [verifying, setVerifying] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [shakes, setShakes] = useState(0);
  const [resent, setResent] = useState(false);
  const [resendIn, restartResend] = useResendCountdown();
  const boxes = useRef([]);
  const emailInput = useRef(null);
  const id = `sic-${portal}`;

  const go = (next, dir) => {
    setDirection(dir);
    setStep(next);
  };

  useEffect(() => {
    if (!resent) return undefined;
    const timer = setTimeout(() => setResent(false), 2600);
    return () => clearTimeout(timer);
  }, [resent]);

  const focusBox = (index) => {
    const box = boxes.current[Math.max(0, Math.min(CODE_LENGTH - 1, index))];
    if (box) {
      box.focus();
      box.select?.();
    }
  };

  const sendCode = async (e) => {
    e?.preventDefault();
    if (sending) return;
    const value = email.trim();
    if (!isEmail(value)) {
      setEmailError("Enter the email you use for Anna.");
      emailInput.current?.focus();
      return;
    }
    setSending(true);
    setEmailError("");
    setCodeError("");
    try {
      const data = await requestSignInCode(value, portal);
      setTicket(data);
      setDigits(EMPTY);
      restartResend();
      if (step === "code") {
        setResent(true);
        focusBox(0);
      } else {
        go("code", 1);
      }
    } catch (err) {
      const message = codeErrorMessage(err, "We couldn't send the code. Try again in a few minutes.");
      if (step === "code") setCodeError(message);
      else setEmailError(message);
    } finally {
      setSending(false);
    }
  };

  const verify = async (code) => {
    if (verifying || !ticket) return;
    setVerifying(true);
    setCodeError("");
    try {
      const tokens = await verifySignInCode(ticket.token, code);
      go("done", 1);
      await onSignedIn(tokens);
    } catch (err) {
      setStep("code");
      setCodeError(codeErrorMessage(err));
      setShakes((n) => n + 1);
      setDigits(EMPTY);
      setTimeout(() => focusBox(0), 50);
    } finally {
      setVerifying(false);
    }
  };

  // Typing, pasting or an autofilled code: spread the digits from this box onwards
  const fill = (index, raw) => {
    const typed = String(raw || "").replace(/\D/g, "");
    setCodeError("");
    const next = [...digits];
    if (!typed) {
      next[index] = "";
      setDigits(next);
      return;
    }
    typed.slice(0, CODE_LENGTH - index).split("").forEach((digit, offset) => {
      next[index + offset] = digit;
    });
    setDigits(next);
    const firstEmpty = next.findIndex((d) => !d);
    if (firstEmpty === -1) {
      boxes.current[CODE_LENGTH - 1]?.blur();
      verify(next.join(""));
    } else {
      focusBox(Math.min(index + typed.length, firstEmpty));
    }
  };

  const onBoxKey = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      e.preventDefault();
      const next = [...digits];
      next[index - 1] = "";
      setDigits(next);
      focusBox(index - 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusBox(index - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusBox(index + 1);
    }
  };

  const slide = {
    enter: (dir) => ({ opacity: 0, x: reduce ? 0 : dir * 32 }),
    center: { opacity: 1, x: 0 },
    exit: (dir) => ({ opacity: 0, x: reduce ? 0 : dir * -32 }),
  };
  const stepMotion = {
    custom: direction,
    variants: slide,
    initial: "enter",
    animate: "center",
    exit: "exit",
    transition: { duration: reduce ? 0.12 : 0.26, ease: EASE },
  };
  const code = digits.join("");

  return (
    <motion.div className={`sic is-${variant}`} layout={!reduce} transition={{ duration: 0.25, ease: EASE }}>
      <AnimatePresence mode="wait" initial={false} custom={direction}>
        {step === "email" && (
          <motion.form key="email" className="sic-step" onSubmit={sendCode} noValidate {...stepMotion}>
            <motion.span
              className="sic-tile"
              initial={reduce ? false : { scale: 0.6, rotate: -8 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 18 }}
            >
              <Icon name="mail" size={26} />
            </motion.span>
            <h2 className="sic-title">Sign in with a code</h2>
            <p className="sic-text">
              Type your email and we&apos;ll send you a 6-digit code. You won&apos;t need your password.
            </p>
            <label className="sic-label" htmlFor={`${id}-email`}>Email</label>
            <div className={`sic-input${emailError ? " is-error" : ""}`}>
              <Icon name="mail" size={18} />
              <input
                ref={emailInput}
                id={`${id}-email`}
                type="email"
                autoComplete="email"
                placeholder={variant === "staff" ? "counselor@school.edu.ph" : "you@example.com"}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError("");
                }}
                autoFocus
                aria-invalid={Boolean(emailError)}
                aria-describedby={emailError ? `${id}-email-error` : undefined}
              />
            </div>
            <AnimatePresence>
              {emailError && (
                <motion.p
                  id={`${id}-email-error`}
                  className="sic-error"
                  role="alert"
                  initial={{ opacity: 0, y: -4, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  {emailError}
                </motion.p>
              )}
            </AnimatePresence>
            <button type="submit" className="sic-btn" disabled={sending}>
              {sending ? (
                <>
                  <Spinner />
                  Sending your code…
                </>
              ) : (
                <>
                  Send me a code
                  <Icon name="send" size={18} />
                </>
              )}
            </button>
            <button type="button" className="sic-link sic-back" onClick={() => onBack?.(email.trim())}>
              <Icon name="arrow-left" size={16} />
              Use my password instead
            </button>
          </motion.form>
        )}

        {step === "code" && (
          <motion.form
            key="code"
            className="sic-step"
            onSubmit={(e) => {
              e.preventDefault();
              if (code.length === CODE_LENGTH) verify(code);
            }}
            noValidate
            {...stepMotion}
          >
            <motion.span
              className="sic-tile"
              initial={reduce ? false : { scale: 0.6, y: 6 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 16 }}
            >
              <Icon name="mail-check" size={26} />
            </motion.span>
            <h2 className="sic-title">Check your email</h2>
            <p className="sic-text">
              We sent a 6-digit code to <b>{ticket?.sent_to}</b>. It works for 10 minutes.
            </p>
            <motion.div
              key={shakes}
              className="sic-otp"
              role="group"
              aria-label="6-digit sign-in code"
              animate={shakes && !reduce ? { x: [0, -12, 11, -8, 7, -4, 3, 0] } : { x: 0 }}
              transition={{ duration: 0.45 }}
            >
              {digits.map((digit, index) => (
                <motion.input
                  key={index}
                  ref={(el) => { boxes.current[index] = el; }}
                  className={`sic-box${digit ? " is-filled" : ""}${codeError ? " is-error" : ""}`}
                  inputMode="numeric"
                  autoComplete={index === 0 ? "one-time-code" : "off"}
                  maxLength={CODE_LENGTH}
                  value={digit}
                  onChange={(e) => fill(index, e.target.value)}
                  onKeyDown={(e) => onBoxKey(index, e)}
                  onFocus={(e) => e.target.select()}
                  onPaste={(e) => {
                    e.preventDefault();
                    fill(index, e.clipboardData.getData("text"));
                  }}
                  autoFocus={index === 0}
                  disabled={verifying}
                  aria-label={`Digit ${index + 1}`}
                  initial={reduce ? false : { opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: reduce ? 0 : 0.05 + index * 0.04, duration: 0.25, ease: EASE }}
                />
              ))}
            </motion.div>
            <AnimatePresence mode="wait">
              {codeError ? (
                <motion.p key="error" className="sic-error" role="alert" initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  {codeError}
                </motion.p>
              ) : resent ? (
                <motion.p key="resent" className="sic-ok" role="status" initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <Icon name="check" size={16} /> New code sent. Use the newest email.
                </motion.p>
              ) : null}
            </AnimatePresence>
            <button type="submit" className="sic-btn" disabled={verifying || code.length !== CODE_LENGTH}>
              {verifying ? (
                <>
                  <Spinner />
                  Checking…
                </>
              ) : (
                <>
                  Sign in
                  <Icon name="sign-in" size={18} />
                </>
              )}
            </button>
            <div className="sic-row">
              <span>Didn&apos;t get it?</span>
              {resendIn > 0 ? (
                <span className="sic-count">
                  <CountdownRing left={resendIn} />
                  Send again in {formatSeconds(resendIn)}
                </span>
              ) : (
                <button type="button" className="sic-link" onClick={sendCode} disabled={sending}>
                  {sending ? "Sending…" : "Send a new code"}
                </button>
              )}
            </div>
            <div className="sic-row">
              <button type="button" className="sic-link" onClick={() => go("email", -1)}>
                Change email
              </button>
              <span aria-hidden="true">·</span>
              <button type="button" className="sic-link" onClick={() => onBack?.(email.trim())}>
                Use my password instead
              </button>
            </div>
          </motion.form>
        )}

        {step === "done" && (
          <motion.div key="done" className="sic-step sic-done" role="status" {...stepMotion}>
            <motion.span
              className="sic-tile is-ok"
              initial={reduce ? false : { scale: 0.4 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 420, damping: 15 }}
            >
              <Icon name="check" size={30} />
            </motion.span>
            <h2 className="sic-title">You&apos;re in</h2>
            <p className="sic-text">
              {variant === "staff" ? "Opening your dashboard…" : "Opening your chat…"}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SignInCodeFlow;
